import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, Form, Input, Select, Space, Button, Modal, message } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import ListItem from '@tiptap/extension-list-item';
import Underline from '@tiptap/extension-underline';
import Highlight from '@tiptap/extension-highlight';
import Link from '@tiptap/extension-link';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import { getNoticeTemplateContent } from '../../services/debt';
import {
  FontFamily,
  FontSize,
  StyledParagraph,
  StyledHeading,
  StyledBulletList,
  StyledOrderedList,
} from './extensions';
import {
  mmToPx,
  extractPageSettingsFromHtml,
  splitHeaderBodyFooter,
  composeHtml,
  extractOuterShell,
  unwrapOuterShell,
  wrapOuterShell,
  normalizeAsposeLists,
  normalizeInlineFonts,
  extractPlaceholders,
  highlightAndFill,
  paginateEntire,
  sanitizeHtmlForPreview,
} from './utils';

// 组件：NoticeDocEditor
// 职责：基于 TipTap 富文本编辑器实现催告模板的编辑与预览，支持：
// - 模板加载：支持传入 initialHtml 或通过 templateId 拉取模板内容
// - 结构拆分：识别并拆分页眉/正文/页脚，正文自动规范化列表/字体
// - 占位符：抽取 {{key}} 形式的占位符并提供填写面板；预览时高亮未填写项
// - 编辑模式：提供正文/页眉/页脚三个独立编辑器，支持标题/段落/列表/对齐/字体/字号等操作
// - 分页预览：根据页面尺寸与页边距计算分页，并生成每页内容，以便打印与导出
// - 状态保存：会话态保存最近一次编辑与填写状态，便于返回继续编辑
const KEY = 'swy_publish_notice_state';
// 这里与 DebtNotice 发布流程使用同一个 KEY：编辑页与签章页之间通过 sessionStorage 共享状态
const loadState = () => { try { return JSON.parse(sessionStorage.getItem(KEY) || '{}'); } catch (_) { return {}; } };
const saveState = (next) => { const prev = loadState(); sessionStorage.setItem(KEY, JSON.stringify({ ...prev, ...next })); };

const NoticeDocEditor = ({
  initialHtml,
  templateId,
  initialValues,
  initialPageSettings,
  values: valuesProp,
  onValuesChange,
  onAutoFill,
  onPlaceholdersChange,
  actionRef,
  readOnly,
  hideFillPanel,
  hideReload,
  hideAutoFill,
  hideSave,
  onSaved,
  renderPageOverlay,
  overlayInteractive,
}) => {
  /**
   * 状态说明：
   * - contentHtml：完整模板组合（页眉+正文+页脚）
   * - headerHtml/bodyHtml/footerHtml：拆分后的三段内容，便于独立编辑
   * - placeholders：从模板中抽取到的占位符键名数组
   * - values：占位符填充值映射 {key: value}
   * - pageSettings：页面尺寸与边距 mm 单位；可从模板 CSS @page 中提取
   * - viewMode：'preview' 或 'edit' 两种模式
   * - editHtml：编辑模式下正文的当前 HTML（用于分页参考）
   * - fontFamily/fontSize：工具栏选择的字体与字号（应用到选区或全文）
   * - pageGuides：预估分页的辅助线位置（px），用于在编辑模式参考
   * - activeEditorKey：当前聚焦的编辑器 'body' | 'header' | 'footer'
   */
  const [contentHtml, setContentHtml] = useState(initialHtml || loadState().contentHtml || '');
  const [headerHtml, setHeaderHtml] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [footerHtml, setFooterHtml] = useState('');
  const [headerShell, setHeaderShell] = useState(null);
  const [footerShell, setFooterShell] = useState(null);
  const [placeholders, setPlaceholders] = useState([]);
  const [innerValues, setInnerValues] = useState(initialValues || loadState().values || {});
  const [pageSettings, setPageSettings] = useState(initialPageSettings || loadState().pageSettings || { widthMm: 210, heightMm: 297, marginTopMm: 25, marginRightMm: 25, marginBottomMm: 25, marginLeftMm: 25 });
  const [viewMode, setViewMode] = useState('preview');
  const [editHtml, setEditHtml] = useState('');
  const [fontFamily, setFontFamily] = useState();
  const [fontSize, setFontSize] = useState();
  const [pageGuides, setPageGuides] = useState([]);
  const [activeEditorKey, setActiveEditorKey] = useState('body');
  const [helpOpen, setHelpOpen] = useState(false);

  const isControlled = valuesProp !== undefined;
  const values = isControlled ? (valuesProp || {}) : innerValues;
  const updateValues = (nextValues) => {
    if (!isControlled) setInnerValues(nextValues);
    if (onValuesChangeRef.current) onValuesChangeRef.current(nextValues);
  };

  const onValuesChangeRef = useRef(onValuesChange);
  useEffect(() => { onValuesChangeRef.current = onValuesChange; }, [onValuesChange]);

  const headerShellRef = useRef(headerShell);
  const footerShellRef = useRef(footerShell);
  useEffect(() => { headerShellRef.current = headerShell; }, [headerShell]);
  useEffect(() => { footerShellRef.current = footerShell; }, [footerShell]);
  const initAppliedRef = useRef('');
  const dirtyRef = useRef(false);

  const markDirty = () => {
    // 只要编辑器发生变更，就将“保存状态”置为 false
    // 发布流程会根据 saved 标志控制是否允许进入下一步（例如签章页要求内容已保存）
    if (readOnly) return;
    if (dirtyRef.current) return;
    dirtyRef.current = true;
    saveState({ saved: false });
  };

  // 初始化：加载模板 HTML（优先使用传入 initialHtml，其次 templateId 获取）
  useEffect(() => {
    // 将拉取到的 HTML 应用到组件状态：拆分三段、规范化正文列表、抽取占位符、同步页面设置
    const applyHtml = (html) => {
      // 页眉/页脚可能带外层结构（例如固定高度容器），需要保留 shell 以避免布局抖动
      const parts = splitHeaderBodyFooter(html);
      const headerInfo = extractOuterShell(parts.header);
      const footerInfo = extractOuterShell(parts.footer);
      const bodyFixed = normalizeAsposeLists(parts.body);
      setHeaderShell(headerInfo.shell);
      setFooterShell(footerInfo.shell);
      setHeaderHtml(wrapOuterShell(headerInfo.innerHtml, headerInfo.shell));
      setBodyHtml(bodyFixed);
      setFooterHtml(wrapOuterShell(footerInfo.innerHtml, footerInfo.shell));
      setContentHtml(composeHtml(wrapOuterShell(headerInfo.innerHtml, headerInfo.shell), bodyFixed, wrapOuterShell(footerInfo.innerHtml, footerInfo.shell)));
      const ph = extractPlaceholders(html);
      setPlaceholders(ph);
      onPlaceholdersChange && onPlaceholdersChange(ph);
      const vs = { ...(values || {}), ...(initialValues || {}) };
      Object.keys(vs).forEach(k => { if (!ph.includes(k)) delete vs[k]; });
      ph.forEach(k => { if (!(k in vs)) vs[k] = ''; });
      updateValues(vs);
      const ps = extractPageSettingsFromHtml(html);
      if (ps) setPageSettings(ps);
    };

    const initial = String(initialHtml || '');
    const hasInitial = !!initial.trim();
    // 用一个轻量签名避免重复应用：防止父组件频繁 re-render 导致内容被反复重置
    const sig = hasInitial
      ? `initial:${String(templateId || '')}:${initial.length}:${initial.slice(0, 80)}`
      : (templateId ? `template:${String(templateId)}` : '');
    if (sig && initAppliedRef.current === sig) return;
    initAppliedRef.current = sig;

    if (hasInitial) {
      applyHtml(initial);
      // initialHtml 的场景通常是“回填草稿/历史内容”，默认为未保存状态，需用户显式保存
      saveState({ loadedTemplateId: templateId || null, ...(readOnly ? {} : { saved: false }) });
      dirtyRef.current = false;
      return;
    }

    if (templateId) {
      getNoticeTemplateContent(templateId, 'html')
        .then(({ data }) => {
          const html = String(data?.html || '');
          applyHtml(html);
          if (data && data.pageSettings) setPageSettings(data.pageSettings);
          // templateId 拉取的模板同样视为“刚加载未保存”，避免误以为可以直接进入下一步
          saveState({ loadedTemplateId: templateId, ...(readOnly ? {} : { saved: false }) });
          dirtyRef.current = false;
        })
        .catch(() => {});
    }
  }, [initialHtml, templateId, readOnly]);

  // 预览 HTML（占位符高亮与填充）
  const previewHtml = useMemo(() => highlightAndFill(contentHtml, values), [contentHtml, values, placeholders]);

  // TipTap 编辑器：正文
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3, 4, 5, 6] } }),
      StyledParagraph,
      StyledHeading,
      StyledBulletList,
      StyledOrderedList,
      ListItem,
      Underline,
      Highlight,
      Link.configure({ openOnClick: false }),
      FontFamily,
      FontSize,
      TextStyle,
      Color,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Placeholder.configure({ placeholder: '在此编辑文档内容' }),
    ],
    content: '',
    editorProps: { attributes: { class: 'tiptap-content', style: 'outline:none;width:100%;' } },
    onUpdate({ editor }) {
      markDirty();
      // 编辑正文时，更新分页参考线（pageGuides），用于编辑模式对页内高度的预估
      try {
        const htmlCur = editor.getHTML();
        setEditHtml(htmlCur);
        const s = pageSettings || { widthMm: 210, heightMm: 297, marginTopMm: 25, marginRightMm: 25, marginBottomMm: 25, marginLeftMm: 25 };
        const contentH = mmToPx(s.heightMm - s.marginTopMm - s.marginBottomMm);
        const pcount = paginateEntire(htmlCur || '', headerHtml || '', footerHtml || '', s).length;
        const guides = [];
        for (let i = 1; i < pcount; i++) guides.push(i * contentH);
        setPageGuides(guides);
      } catch (_) {}
    },
  });

  // 页眉/页脚编辑器
  const headerEditor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3, 4, 5, 6] } }),
      StyledParagraph,
      StyledHeading,
      StyledBulletList,
      StyledOrderedList,
      ListItem,
      Underline,
      Highlight,
      Link.configure({ openOnClick: false }),
      FontFamily,
      FontSize,
      TextStyle,
      Color,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Placeholder.configure({ placeholder: '编辑页眉内容' }),
    ],
    content: '',
    editorProps: { attributes: { class: 'tiptap-content', style: 'outline:none;width:100%;' } },
    onUpdate({ editor }) {
      markDirty();
      // 编辑页眉触发重新测量页眉/页脚高度，并更新分页辅助线
      try {
        const htmlCur = editor.getHTML();
        const wrappedHeader = wrapOuterShell(htmlCur, headerShellRef.current);
        setHeaderHtml(wrappedHeader);
        const s = pageSettings || { widthMm: 210, heightMm: 297, marginTopMm: 25, marginRightMm: 25, marginBottomMm: 25, marginLeftMm: 25 };
        const contentH = mmToPx(s.heightMm - s.marginTopMm - s.marginBottomMm);
        const pcount = paginateEntire(bodyHtml || '', wrappedHeader || '', footerHtml || '', s).length;
        const guides = [];
        for (let i = 1; i < pcount; i++) guides.push(i * contentH);
        setPageGuides(guides);
      } catch (_) {}
    },
  });

  const footerEditor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3, 4, 5, 6] } }),
      StyledParagraph,
      StyledHeading,
      StyledBulletList,
      StyledOrderedList,
      ListItem,
      Underline,
      Highlight,
      Link.configure({ openOnClick: false }),
      FontFamily,
      FontSize,
      TextStyle,
      Color,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Placeholder.configure({ placeholder: '编辑页脚内容' }),
    ],
    content: '',
    editorProps: { attributes: { class: 'tiptap-content', style: 'outline:none;width:100%;' } },
    onUpdate({ editor }) {
      markDirty();
      // 编辑页脚触发重新测量页眉/页脚高度，并更新分页辅助线
      try {
        const htmlCur = editor.getHTML();
        const wrappedFooter = wrapOuterShell(htmlCur, footerShellRef.current);
        setFooterHtml(wrappedFooter);
        const s = pageSettings || { widthMm: 210, heightMm: 297, marginTopMm: 25, marginRightMm: 25, marginBottomMm: 25, marginLeftMm: 25 };
        const contentH = mmToPx(s.heightMm - s.marginTopMm - s.marginBottomMm);
        const pcount = paginateEntire(bodyHtml || '', headerHtml || '', wrappedFooter || '', s).length;
        const guides = [];
        for (let i = 1; i < pcount; i++) guides.push(i * contentH);
        setPageGuides(guides);
      } catch (_) {}
    },
  });

  useEffect(() => {
    // 监听三个编辑器的 focus 事件，记录当前激活编辑器，供工具栏操作使用
    try {
      editor?.on('focus', () => setActiveEditorKey('body'));
      headerEditor?.on('focus', () => setActiveEditorKey('header'));
      footerEditor?.on('focus', () => setActiveEditorKey('footer'));
    } catch (_) {}
  }, [editor, headerEditor, footerEditor]);

  // 正文或填写项变更时重新分页
  const pages = useMemo(() => {
    try {
      const s = pageSettings || { widthMm: 210, heightMm: 297, marginTopMm: 25, marginRightMm: 25, marginBottomMm: 25, marginLeftMm: 25 };
      return paginateEntire(highlightAndFill(bodyHtml || '', values), headerHtml || '', footerHtml || '', s);
    } catch (_) {
      return [highlightAndFill(bodyHtml || '', values)];
    }
  }, [pageSettings, bodyHtml, headerHtml, footerHtml, values]);

  const safeHeaderHtml = useMemo(() => sanitizeHtmlForPreview(headerHtml || ''), [headerHtml]);
  const safeFooterHtml = useMemo(() => sanitizeHtmlForPreview(footerHtml || ''), [footerHtml]);
  const safePages = useMemo(() => (pages || []).map(p => sanitizeHtmlForPreview(p)), [pages]);

  // 进入编辑模式：规范化字体/列表并填充三个编辑器
  const openEdit = () => {
    // 规范化：将 Word/Aspose 导出的字体/列表转换为更易编辑的结构
    const headerInner = unwrapOuterShell(headerHtml || '', headerShell);
    const footerInner = unwrapOuterShell(footerHtml || '', footerShell);
    const normalizedHeader = normalizeAsposeLists(normalizeInlineFonts(headerInner || ''));
    const normalizedFooter = normalizeAsposeLists(normalizeInlineFonts(footerInner || ''));
    const normalizedBody = normalizeAsposeLists(normalizeInlineFonts(bodyHtml || contentHtml || ''));
    setEditHtml(normalizedBody);
    setViewMode('edit');
    setTimeout(() => {
      // 设置编辑器内容并聚焦到正文末尾，提升编辑体验
      try { editor?.commands.setContent(normalizedBody, false); } catch (_) {}
      try { headerEditor?.commands.setContent(normalizedHeader || '', false); } catch (_) {}
      try { footerEditor?.commands.setContent(normalizedFooter || '', false); } catch (_) {}
      try { editor?.commands.focus('end'); } catch (_) {}
    }, 0);
    try {
      // 根据规范化后的内容重新计算分页辅助线
      const s = pageSettings || { widthMm: 210, heightMm: 297, marginTopMm: 25, marginRightMm: 25, marginBottomMm: 25, marginLeftMm: 25 };
      const wrappedHeader = wrapOuterShell(normalizedHeader || '', headerShell);
      const wrappedFooter = wrapOuterShell(normalizedFooter || '', footerShell);
      const contentH = mmToPx(s.heightMm - s.marginTopMm - s.marginBottomMm);
      const pcount = paginateEntire(normalizedBody || '', wrappedHeader || '', wrappedFooter || '', s).length;
      const guides = [];
      for (let i = 1; i < pcount; i++) guides.push(i * contentH);
      setPageGuides(guides);
    } catch (_) {}
  };

  // 保存编辑：抓取三段 HTML → 列表/字体规范化 → 组装与占位符同步
  const saveEdit = () => {
    // 从编辑器读取当前 HTML；为空时回退为既有内容
    const bodyRaw = editor?.getHTML?.() || editHtml || '';
    const headerRaw = headerEditor?.getHTML?.() || unwrapOuterShell(headerHtml || '', headerShell) || '';
    const footerRaw = footerEditor?.getHTML?.() || unwrapOuterShell(footerHtml || '', footerShell) || '';
    // 为避免不同来源的字体表现差异，统一将计算后的字体/字号写入内联样式
    const headerFonts = normalizeInlineFonts(headerRaw);
    const footerFonts = normalizeInlineFonts(footerRaw);
    const bodyFixed = normalizeAsposeLists(bodyRaw);
    const headerFixed = wrapOuterShell(normalizeAsposeLists(headerFonts), headerShell);
    const footerFixed = wrapOuterShell(normalizeAsposeLists(footerFonts), footerShell);
    setHeaderHtml(headerFixed);
    setBodyHtml(bodyFixed);
    setFooterHtml(footerFixed);
    const composed = `${headerFixed || ''}${bodyFixed || ''}${footerFixed || ''}`;
    setContentHtml(composed);
    if (!readOnly) saveState({ contentHtml: composed, pageSettings, saved: false });
    // 同步占位符集合：移除已删除的键，新增未填写的键，并重置填写面板
    const ph = extractPlaceholders(composed);
    setPlaceholders(ph);
    onPlaceholdersChange && onPlaceholdersChange(ph);
    const nextVals = { ...(values || {}) };
    Object.keys(nextVals).forEach(k => { if (!ph.includes(k)) delete nextVals[k]; });
    ph.forEach(k => { if (!(k in nextVals)) nextVals[k] = ''; });
    updateValues(nextVals);
    setViewMode('preview');
  };

  // 编辑容器基础字体与行高（提升输入体验）
  useEffect(() => {
    const root = document.querySelector('.tiptap-content');
    if (root && viewMode === 'edit') {
      const s = String(contentHtml || headerHtml || '');
      const ff = (s.match(/font-family:\s*([^;"']+)/i)?.[1] || '').trim() || 'Noto Serif SC';
      root.style.fontFamily = ff;
      root.style.lineHeight = '1.5';
    }
  }, [viewMode, contentHtml, headerHtml]);

  // 工具栏动作：聚焦于当前激活编辑器
  const getActiveEditor = () => {
    if (activeEditorKey === 'header') return headerEditor;
    if (activeEditorKey === 'footer') return footerEditor;
    return editor;
  };
  // 常用块级/行内格式操作；尽量通过 chain() 保持焦点与选区
  const setH1 = () => { try { getActiveEditor()?.chain().focus().toggleHeading({ level: 1 }).run(); } catch (_) {} };
  const setH2 = () => { try { getActiveEditor()?.chain().focus().toggleHeading({ level: 2 }).run(); } catch (_) {} };
  const setParagraph = () => { try { getActiveEditor()?.chain().focus().setParagraph().run(); } catch (_) {} };
  const toggleBold = () => { try { getActiveEditor()?.chain().focus().toggleBold().run(); } catch (_) {} };
  const toggleItalic = () => { try { getActiveEditor()?.chain().focus().toggleItalic().run(); } catch (_) {} };
  const toggleUnderline = () => { try { getActiveEditor()?.chain().focus().toggleUnderline().run(); } catch (_) {} };
  const toggleUl = () => { try { getActiveEditor()?.chain().focus().toggleBulletList().run(); } catch (_) {} };
  const toggleOl = () => { try { getActiveEditor()?.chain().focus().toggleOrderedList().run(); } catch (_) {} };
  const insertHr = () => { try { getActiveEditor()?.chain().focus().insertContent('<hr style="border:0;border-top:1px dashed #bbb;"/>').run(); } catch (_) {} };
  const insertEmptyLine = () => { try { editor?.chain().focus().insertContent('<p><br/></p>').run(); } catch (_) {} };
  const undo = () => { try { getActiveEditor()?.commands.undo(); } catch (_) {} };
  const redo = () => { try { getActiveEditor()?.commands.redo(); } catch (_) {} };
  const alignLeft = () => { try { getActiveEditor()?.chain().focus().setTextAlign('left').run(); } catch (_) {} };
  const alignCenter = () => { try { getActiveEditor()?.chain().focus().setTextAlign('center').run(); } catch (_) {} };
  const alignRight = () => { try { getActiveEditor()?.chain().focus().setTextAlign('right').run(); } catch (_) {} };
  const setRed = () => { try { getActiveEditor()?.chain().focus().setColor('#d93025').run(); } catch (_) {} };
  const toggleMarkHighlight = () => { try { getActiveEditor()?.chain().focus().toggleHighlight().run(); } catch (_) {} };
  const applyFontFamily = () => {
    const f = String(fontFamily || '').trim();
    if (!f) return;
    try {
      const ae = getActiveEditor();
      const sel = ae?.state?.selection;
      // 若未选择文本，则对全文应用字体；否则仅对选区应用
      if (!sel || sel.empty) {
        ae?.chain().focus().selectAll().setFontFamily(f).run();
        ae?.commands.blur(); ae?.commands.focus('end');
      } else {
        ae?.chain().focus().setFontFamily(f).run();
      }
    } catch (_) {}
  };
  const applyFontSize = () => {
    const s = String(fontSize || '').trim();
    if (!s) return;
    try {
      const ae = getActiveEditor();
      const sel = ae?.state?.selection;
      // 若未选择文本，则对全文应用字号；否则仅对选区应用
      if (!sel || sel.empty) {
        ae?.chain().focus().selectAll().setFontSize(s).run();
        ae?.commands.blur(); ae?.commands.focus('end');
      } else {
        ae?.chain().focus().setFontSize(s).run();
      }
    } catch (_) {}
  };
  const addPlaceholderFromSelection = () => {
    try {
      const ae = getActiveEditor();
      const sel = ae?.state?.selection;
      if (!sel || sel.empty) { message.warning('请先选中要标记的文本'); return; }
      const txt = ae?.state?.doc?.textBetween(sel.from, sel.to, '\n') || '';
      // 将选中文本用 {{key}} 包裹，加入占位符集合（后续可填写并高亮）
      const wrapped = `{{${txt}}}`;
      ae?.chain().focus().insertContent(wrapped).run();
    } catch (_) {}
  };

  // 校验并保存：生成预览 HTML，写入会话态，并通知外部页面
  const handleSave = () => {
    let htmlToSave = contentHtml;
    try {
      if (viewMode === 'edit') {
        const bodyRaw = editor?.getHTML?.() || editHtml || bodyHtml || '';
        const headerRaw = headerEditor?.getHTML?.() || unwrapOuterShell(headerHtml || '', headerShellRef.current) || '';
        const footerRaw = footerEditor?.getHTML?.() || unwrapOuterShell(footerHtml || '', footerShellRef.current) || '';
        const headerFonts = normalizeInlineFonts(headerRaw);
        const footerFonts = normalizeInlineFonts(footerRaw);
        const bodyFixed = normalizeAsposeLists(bodyRaw);
        const headerFixed = wrapOuterShell(normalizeAsposeLists(headerFonts), headerShellRef.current);
        const footerFixed = wrapOuterShell(normalizeAsposeLists(footerFonts), footerShellRef.current);
        htmlToSave = `${headerFixed || ''}${bodyFixed || ''}${footerFixed || ''}`;
        setHeaderHtml(headerFixed);
        setBodyHtml(bodyFixed);
        setFooterHtml(footerFixed);
        setContentHtml(htmlToSave);
        setViewMode('preview');
      } else {
        const composedNow = composeHtml(headerHtml || '', bodyHtml || '', footerHtml || '');
        const draft = loadState()?.contentHtml;
        htmlToSave = (draft && String(draft).trim()) ? String(draft) : composedNow;
      }
    } catch (_) {}

    const previewHtmlNow = highlightAndFill(htmlToSave, values);
    if (!previewHtmlNow || previewHtmlNow.trim().length < 10) {
      message.error('请完善催告内容');
      return;
    }
    const data = { contentHtml: htmlToSave, previewHtml: previewHtmlNow, values, pageSettings };
    saveState({ ...data, saved: true });
    dirtyRef.current = false;
    onSaved && onSaved(data);
    message.success('已校验并保存');
  };

  const handleAutoFill = () => {
    if (!onAutoFill) {
      message.info('智能填充功能暂未实现');
      return;
    }
    try {
      const patch = onAutoFill({ placeholders, values });
      if (!patch || typeof patch !== 'object') {
        message.warning('未获取到可填充的数据');
        return;
      }
      const nextValues = { ...(values || {}) };
      Object.keys(patch).forEach((k) => {
        const cur = String(nextValues[k] || '').trim();
        const nv = String(patch[k] || '').trim();
        if (!cur && nv) nextValues[k] = patch[k];
      });
      updateValues(nextValues);
      message.success('智能填充完成');
    } catch (_) {
      message.error('智能填充失败');
    }
  };

  const handleReload = () => {
    // 重新载入模板：优先使用 templateId 获取的远端 HTML，其次使用 initialHtml 或会话态
    const apply = (html, pageSettingsFromApi) => {
      const parts = splitHeaderBodyFooter(html);
      const headerInfo = extractOuterShell(parts.header);
      const footerInfo = extractOuterShell(parts.footer);
      const bodyFixed = normalizeAsposeLists(parts.body);
      setHeaderShell(headerInfo.shell);
      setFooterShell(footerInfo.shell);
      const headerComposed = wrapOuterShell(headerInfo.innerHtml, headerInfo.shell);
      const footerComposed = wrapOuterShell(footerInfo.innerHtml, footerInfo.shell);
      setHeaderHtml(headerComposed);
      setBodyHtml(bodyFixed);
      setFooterHtml(footerComposed);
      const composed = composeHtml(headerComposed, bodyFixed, footerComposed);
      setContentHtml(composed);
      const ph = extractPlaceholders(html);
      setPlaceholders(ph);
      onPlaceholdersChange && onPlaceholdersChange(ph);
      const vs = {};
      ph.forEach(k => { vs[k] = ''; });
      updateValues(vs);
      let ps = pageSettingsFromApi;
      if (!ps) {
        try { ps = extractPageSettingsFromHtml(html); } catch (_) {}
      }
      if (ps) setPageSettings(ps);
      saveState({ contentHtml: composed, values: vs, pageSettings: ps || pageSettings, saved: false });
      setViewMode('preview');
    };
    const htmlIn = initialHtml || loadState().contentHtml;
    if (templateId) {
      getNoticeTemplateContent(templateId, 'html')
        .then(({ data }) => {
          const html = String(data?.html || '');
          apply(html, data?.pageSettings);
        })
        .catch(() => {});
      return;
    }
    if (htmlIn && String(htmlIn).trim()) {
      apply(String(htmlIn || ''));
    }
  };

  useEffect(() => {
    if (!actionRef) return;
    actionRef.current = {
      reload: handleReload,
      save: handleSave,
    };
    return () => {
      if (actionRef && actionRef.current) actionRef.current = null;
    };
  }, [actionRef, handleReload, handleSave]);

  const safeNumber = (v, fallback) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  };

  const headerDistanceMm = safeNumber(pageSettings?.headerDistanceMm, safeNumber(pageSettings?.marginTopMm, 0));
  const footerDistanceMm = safeNumber(pageSettings?.footerDistanceMm, safeNumber(pageSettings?.marginBottomMm, 0));
  const headerAreaHeightMmRaw = safeNumber(pageSettings?.marginTopMm, 0) - headerDistanceMm;
  const footerAreaHeightMmRaw = safeNumber(pageSettings?.marginBottomMm, 0) - footerDistanceMm;
  const headerAreaHeightMm = headerAreaHeightMmRaw > 1 ? headerAreaHeightMmRaw : safeNumber(pageSettings?.marginTopMm, 0);
  const footerAreaHeightMm = footerAreaHeightMmRaw > 1 ? footerAreaHeightMmRaw : safeNumber(pageSettings?.marginBottomMm, 0);

  return (
    <div>
      <style>{`.tiptap-content ol{list-style-type:decimal;list-style-position:outside;padding-left:18px;margin-left:0;} .tiptap-content ul{list-style-type:disc;list-style-position:outside;padding-left:18px;margin-left:0;} .tiptap-content li p{margin:0;} .tiptap-content hr{border:0;border-top:1px dashed #bbb;} .help-link{display:inline-flex;align-items:center;gap:4px;font-size:12px;color:#1677ff;cursor:pointer;user-select:none;text-decoration:none;} .help-link:hover{color:#4096ff;text-decoration:underline;} .help-link:active{color:#0958d9;}`}</style>
      <div style={{ display:'flex', justifyContent:'flex-start', marginBottom: 12 }}>
        <Space>
          {hideReload ? null : <Button onClick={handleReload}>重新载入</Button>}
          {hideAutoFill ? null : <Button onClick={handleAutoFill}>智能填充</Button>}
          {hideSave ? null : <Button type="primary" onClick={handleSave}>校验并保存</Button>}
        </Space>
      </div>
      <div style={{ display:'flex', gap:12 }}>
        {hideFillPanel ? null : (
          <Card title="填写项" size="small" style={{ flex:'0 0 360px' }}>
            <Form layout="vertical">
              {placeholders.length === 0 ? (
                <div style={{ color:'#999' }}>无可填写项</div>
              ) : placeholders.map(k => (
                <Form.Item key={k} label={String(k).replace(/\*/g,'')}>
                  <Input
                    value={values[k] || ''}
                    onChange={(e) => {
                      const v = e?.target?.value ?? '';
                      const next = { ...(values || {}), [k]: v };
                      updateValues(next);
                    }}
                  />
                </Form.Item>
              ))}
            </Form>
          </Card>
        )}
        <Card
          title={readOnly ? null : (
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <span>文档模板</span>
              <Space>
                <Button type={viewMode==='preview'?'primary':'default'} onClick={()=>setViewMode('preview')}>预览</Button>
                <Button type={viewMode==='edit'?'primary':'default'} onClick={openEdit}>编辑</Button>
                {viewMode==='edit' ? <Button type="primary" onClick={saveEdit}>保存编辑</Button> : null}
                <a
                  className="help-link"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setHelpOpen(true);
                  }}
                >
                  <QuestionCircleOutlined />
                  <span>模板规范</span>
                </a>
              </Space>
            </div>
          )}
          size="small"
          style={{ flex:'1 1 0' }}
        >
          <Modal
            title="模板编辑规范"
            width={720}
            open={helpOpen}
            onOk={() => setHelpOpen(false)}
            onCancel={() => setHelpOpen(false)}
            okText="知道了"
            cancelText="关闭"
            destroyOnClose
          >
            <div style={{ color: '#333', lineHeight: '22px' }}>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>占位符书写与显示规则</div>
              <div style={{ marginBottom: 12 }}>
                <div>1）占位符格式：使用 {"{{...}}"}，避免嵌套大括号与不闭合。</div>
                <div>2）占位符尽量放在同一段文本中，避免被拆成多个样式片段（不同字体/加粗/下划线会导致拆分）。</div>
                <div>3）占位符替换值会自动使用不可折行样式（white-space: nowrap），避免日期/金额被拆行。</div>
                <div>4）日期类值统一展示为 YYYY年MM月DD日（例如：2025年01月10日）。</div>
              </div>

              <div style={{ fontWeight: 600, marginBottom: 8 }}>Word 模板排版规范（避免 PDF 行距/换行异常）</div>
              <div style={{ marginBottom: 12 }}>
                <div>1）段落行距与段前段后：建议统一设置（例如行距 1.5 倍，段前/段后 0），并勾选“不要在同样样式的段落之间添加间距”。</div>
                <div>2）编号段落（重点）：如对行距一致性要求高，优先使用“普通段落 + 手工编号文本（2. / 3. / 4.）+ 悬挂缩进”，少用 Word 自动编号列表。</div>
                <div>3）若必须使用自动编号列表：确保编号段落套用同一列表样式，且该样式中已设置行距/段前段后为目标值。</div>
                <div>4）避免在一句话中插入过多不同样式（频繁切换字体/字号/粗体/下划线），否则转换后会产生大量 span，增加断行/行距差异概率。</div>
                <div>5）避免手工换行（Shift+Enter）与 Tab 对齐，优先用段落/缩进/制表位设置控制版式。</div>
              </div>

              <div style={{ fontWeight: 600, marginBottom: 8 }}>空格与不换行处理（建议在模板中处理）</div>
              <div style={{ marginBottom: 12 }}>
                <div>1）数字+单位：避免写成 “5 日” 这种“数字+普通空格+单位”，建议改为 “5日”。</div>
                <div>2）如必须保留间隔：建议使用不间断空格（Word 中按 Ctrl+Shift+Space），避免在空格处断行。</div>
                <div>3）关键短语需要整体不换行：尽量放在同一文本块中，减少样式拆分。</div>
              </div>

              <div style={{ fontWeight: 600, marginBottom: 8 }}>建议工作流</div>
              <div>
                <div>1）优先在 Word 源模板中完成排版与样式统一，再上传模板。</div>
                <div>2）上传后先在本页预览，再生成 PDF 对照检查（尤其是编号段落、日期、金额、数字+单位）。</div>
                <div>3）如发现“列表段落行距异常”，优先回 Word 按上述“编号段落”规范调整后重新上传。</div>
              </div>
            </div>
          </Modal>
          {viewMode === 'preview' ? (
            <div style={{ display:'flex', flexDirection:'column', gap:12, alignItems:'center' }}>
              {safePages.map((p, idx) => (
                <div key={idx} style={{ width: `${pageSettings.widthMm}mm`, height: `${pageSettings.heightMm}mm`, background:'#fff', border:'1px solid #ddd', boxShadow:'0 2px 6px rgba(0,0,0,.06)', position:'relative' }}>
                  <div style={{ position:'absolute', inset:0 }}>
                    {safeHeaderHtml ? (
                      <div
                        style={{
                          position:'absolute',
                          left: `${pageSettings.marginLeftMm}mm`,
                          right: `${pageSettings.marginRightMm}mm`,
                          top: `${headerDistanceMm}mm`,
                          height: `${headerAreaHeightMm}mm`,
                          overflow:'hidden',
                          boxSizing:'border-box',
                          zIndex: 1,
                        }}
                        dangerouslySetInnerHTML={{ __html: safeHeaderHtml }}
                      />
                    ) : null}
                    <div
                      style={{
                        position:'absolute',
                        left: `${pageSettings.marginLeftMm}mm`,
                        right: `${pageSettings.marginRightMm}mm`,
                        top: `${pageSettings.marginTopMm}mm`,
                        bottom: `${pageSettings.marginBottomMm}mm`,
                        overflow:'hidden',
                        boxSizing:'border-box',
                        zIndex: 0,
                      }}
                      dangerouslySetInnerHTML={{ __html: p }}
                    />
                    {safeFooterHtml ? (
                      <div
                        style={{
                          position:'absolute',
                          left: `${pageSettings.marginLeftMm}mm`,
                          right: `${pageSettings.marginRightMm}mm`,
                          bottom: `${footerDistanceMm}mm`,
                          height: `${footerAreaHeightMm}mm`,
                          overflow:'hidden',
                          boxSizing:'border-box',
                          zIndex: 1,
                        }}
                        dangerouslySetInnerHTML={{ __html: safeFooterHtml }}
                      />
                    ) : null}
                  </div>
                  {typeof renderPageOverlay === 'function'
                    ? (
                      <div style={{ position: 'absolute', inset: 0, pointerEvents: overlayInteractive ? 'auto' : 'none' }}>
                        {renderPageOverlay({ pageIndex: idx, pageCount: pages.length, pageSettings })}
                      </div>
                    )
                    : null}
                  <div style={{ position:'absolute', right:'8mm', bottom:'8mm', fontSize:12, color:'#999' }}>{idx + 1}</div>
                </div>
              ))}
            </div>
          ) : (
            <>
              <div style={{ display:'flex', gap:8, flexWrap:'wrap', padding:'8px 0' }}>
                <Select placeholder="字体" value={fontFamily} onChange={setFontFamily} style={{ width: 150 }} options={[
                  { value: 'Noto Serif SC', label: '思源宋体 Noto Serif SC' },
                  { value: 'Source Han Serif SC', label: '思源宋体 Source Han Serif SC' },
                  { value: 'SimSun', label: '宋体 SimSun' },
                  { value: 'Songti SC', label: '宋体-简 Songti SC' },
                  { value: 'Times New Roman', label: 'Times New Roman' },
                ]} />
                <Select placeholder="字号" value={fontSize} onChange={setFontSize} style={{ width: 70 }} options={[
                  { value: '12px', label: '12px' },
                  { value: '14px', label: '14px' },
                  { value: '16px', label: '16px' },
                  { value: '18px', label: '18px' },
                  { value: '20px', label: '20px' },
                  { value: '22px', label: '22px' },
                  { value: '24px', label: '24px' },
                  { value: '26px', label: '26px' },
                ]} />
                <Button onClick={applyFontFamily}>应用字体</Button>
                <Button onClick={applyFontSize}>应用字号</Button>
                <Button onClick={setParagraph}>正文</Button>
                <Button onClick={setH1}>H1</Button>
                <Button onClick={setH2}>H2</Button>
                <Button onClick={toggleBold}>粗体</Button>
                <Button onClick={toggleItalic}>斜体</Button>
                <Button onClick={toggleUnderline}>下划线</Button>
                <Button onClick={toggleMarkHighlight}>高亮</Button>
                <Button onClick={setRed}>红色</Button>
                <Button onClick={alignLeft}>左对齐</Button>
                <Button onClick={alignCenter}>居中</Button>
                <Button onClick={alignRight}>右对齐</Button>
                <Button onClick={toggleUl}>项目符号</Button>
                <Button onClick={toggleOl}>编号</Button>
                <Button onClick={insertHr}>分页/割线</Button>
                <Button onClick={insertEmptyLine}>空行</Button>
                <Button onClick={addPlaceholderFromSelection}>占位符</Button>
                <Button onClick={undo}>撤销</Button>
                <Button onClick={redo}>重做</Button>
              </div>
              <div style={{ display:'flex', justifyContent:'center' }}>
                <div style={{ width: `${pageSettings.widthMm}mm`, height: `${pageSettings.heightMm}mm`, background:'#fff', border:'1px solid #ddd', boxShadow:'0 2px 6px rgba(0,0,0,.06)', position:'relative' }}>
                  <div style={{ position:'absolute', inset:0 }}>
                    <div
                      style={{
                        position:'absolute',
                        left: `${pageSettings.marginLeftMm}mm`,
                        right: `${pageSettings.marginRightMm}mm`,
                        top: `${headerDistanceMm}mm`,
                        height: `${headerAreaHeightMm}mm`,
                        overflow:'auto',
                        boxSizing:'border-box',
                      }}
                    >
                      <EditorContent editor={headerEditor} />
                    </div>
                    <div
                      style={{
                        position:'absolute',
                        left: `${pageSettings.marginLeftMm}mm`,
                        right: `${pageSettings.marginRightMm}mm`,
                        top: `${pageSettings.marginTopMm}mm`,
                        bottom: `${pageSettings.marginBottomMm}mm`,
                        overflow:'auto',
                        boxSizing:'border-box',
                      }}
                    >
                      <div style={{ position:'relative', minHeight:'100%' }}>
                        <EditorContent editor={editor} />
                        <div style={{ position:'absolute', left:0, right:0, top:0, pointerEvents:'none' }}>
                          {pageGuides.map((y, idx) => (
                            <div key={idx} style={{ position:'absolute', left:0, right:0, top:y+'px', borderTop:'1px dashed #bbb' }} />
                          ))}
                        </div>
                      </div>
                    </div>
                    <div
                      style={{
                        position:'absolute',
                        left: `${pageSettings.marginLeftMm}mm`,
                        right: `${pageSettings.marginRightMm}mm`,
                        bottom: `${footerDistanceMm}mm`,
                        height: `${footerAreaHeightMm}mm`,
                        overflow:'auto',
                        boxSizing:'border-box',
                      }}
                    >
                      <EditorContent editor={footerEditor} />
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </Card>
      </div>

    </div>
  );
};

export default NoticeDocEditor;
